'use client'

import { useState, useEffect } from 'react'
import { getAudioRecorderService, AudioRecorderState } from '@/lib/audio/audio-recorder-service'
import { createClient } from '@/lib/supabase/client'
import { formatDuration } from '@/lib/audio/audio-types'

export function GlobalRecordingOverlay() {
  const [state, setState] = useState<AudioRecorderState>(
    getAudioRecorderService().getState()
  )
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    const service = getAudioRecorderService()
    const unsubscribe = service.subscribe(setState)
    return unsubscribe
  }, [])

  const handleStop = async () => {
    const service = getAudioRecorderService()
    const recording = await service.stopRecording()
    
    if (recording) {
      setIsUploading(true)
      const supabase = createClient()
      const { data: { user } } = await supabase.auth.getUser()
      
      if (user) {
        await service.uploadRecording(recording, user.id)
      }
      setIsUploading(false)
    }
  }

  if (!state.isRecording) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 flex flex-col items-center space-y-6 max-w-sm w-full mx-4">
        <div className="relative">
          <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-20" />
          <div className="relative w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <div className="w-10 h-10 bg-red-600 rounded-full animate-pulse" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Recording Audio
          </h3>
          <div className="text-3xl font-mono font-medium text-gray-700 dark:text-gray-300">
            {formatDuration(state.recordingDuration)}
          </div>
        </div>

        {/* Audio Visualizer Bar */}
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-red-500 transition-all duration-75 ease-out"
            style={{ width: `${state.audioLevel * 100}%` }}
          />
        </div>

        <button
          onClick={handleStop}
          disabled={isUploading}
          className="w-full py-3 px-6 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isUploading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              Stop Recording
            </>
          )}
        </button>
      </div>
    </div>
  )
}
